import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  FormControlLabel,
  InputAdornment,
  Tooltip,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
  Select,
  MenuItem
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LinkIcon from "@mui/icons-material/Link";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UserNeedsOrbitChart from "../../components/user-needs/UserNeedsOrbitChart";
import { USER_NEEDS_MODELS, USER_NEEDS } from "../../components/user-needs/userNeedsConstants";
import completionsApi from "../../server/completionsApi";

function getEmptyScores(model) {
  return Object.fromEntries(USER_NEEDS_MODELS[model].map(n => [n.key, 0]));
}

const MAX_URLS = 5;

export default function UserNeedsChartPage() {
  const [model, setModel] = useState("news");
  const [inputMode, setInputMode] = useState("text");

  // Input
  const [urls, setUrls] = useState([""]);
  const [article, setArticle] = useState("");

  // Targeting
  const [targetingEnabled, setTargetingEnabled] = useState(false);
  const modelTargetKeys = m => USER_NEEDS_MODELS[m].map(n => n.key);
  const firstTargetFor = m => modelTargetKeys(m)[0]; // "update" in both
  const [targetNeed, setTargetNeed] = useState(firstTargetFor("news"));

  // Current view
  const [scores, setScores] = useState(getEmptyScores("news"));
  const [dominant, setDominant] = useState(null);
  const [comments, setComments] = useState([]);
  const [coaching, setCoaching] = useState([]);
  const [headlineExamples, setHeadlineExamples] = useState([]);

  // Stored results (overall + per URL)
  const [overallScores, setOverallScores] = useState(getEmptyScores("news"));
  const [overallDominant, setOverallDominant] = useState(null);
  const [overallComments, setOverallComments] = useState([]);
  const [overallCoaching, setOverallCoaching] = useState([]);
  const [overallHeadlineExamples, setOverallHeadlineExamples] = useState([]);

  const [perUrlResults, setPerUrlResults] = useState([]);
  const [activeView, setActiveView] = useState({ type: "overall", index: -1 });

  const [loading, setLoading] = useState(false);
  const [showGroupLabels, setShowGroupLabels] = useState(true);

  const needs = USER_NEEDS_MODELS[model];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Responsive chart width
  const chartContainerRef = useRef(null);
  const [chartSize, setChartSize] = useState(isMobile ? 300 : 660);

  useEffect(() => {
    if (typeof window === "undefined") return;

    function handleResize() {
      if (chartContainerRef.current) {
        const width = chartContainerRef.current.offsetWidth;
        setChartSize(Math.min(width - 12, 660));
      }
    }

    handleResize(); // run once on mount
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line
  }, [isMobile]);

  // ---------- Helpers ----------
  const cleanUrls = urls => urls.map(u => u.trim()).filter(Boolean).slice(0, MAX_URLS);

  const getHost = (u = "") => {
    try { return new URL(u).host.replace(/^www\./, ""); } catch { return u || "item"; }
  };

  const getNeedLabel = key => USER_NEEDS.find(n => n.key === key)?.label || key;

  // convert any object-shaped list items to strings (prevents React children error)
  const normaliseStrings = arr =>
    (Array.isArray(arr) ? arr : [])
      .map(item => {
        if (item == null) return null;
        if (typeof item === "string") return item;
        if (typeof item === "number" || typeof item === "boolean") return String(item);
        if (typeof item === "object") {
          const candidate =
            item.action ??
            item.tip ??
            item.text ??
            item.note ??
            item.suggestion ??
            item.example ??
            null;
          return candidate != null ? String(candidate) : JSON.stringify(item);
        }
        return null;
      })
      .filter(Boolean);

  function clearResultsOnly() {
    setScores(getEmptyScores(model));
    setDominant(null);
    setComments([]);
    setCoaching([]);
    setHeadlineExamples([]);
    setOverallScores(getEmptyScores(model));
    setOverallDominant(null);
    setOverallComments([]);
    setOverallCoaching([]);
    setOverallHeadlineExamples([]);
    setPerUrlResults([]);
    setActiveView({ type: "overall", index: -1 });
  }

  function applyOverall() {
    setActiveView({ type: "overall", index: -1 });
    setScores(overallScores);
    setDominant(overallDominant);
    setComments(overallComments);
    setCoaching(targetingEnabled ? overallCoaching : []);
    setHeadlineExamples(targetingEnabled ? overallHeadlineExamples : []);
  }

  function applyPerUrl(idx) {
    const item = perUrlResults[idx];
    if (!item) return;
    setActiveView({ type: "per", index: idx });
    setScores(item.scores || getEmptyScores(model));
    setDominant(item.dominant || null);
    const cmt = normaliseStrings(item.comments);
    setComments(cmt.length ? cmt : overallComments);
    const itemCoaching = normaliseStrings(item.coaching);
    const itemHeadlines = normaliseStrings(item.headlineExamples);
    setCoaching(targetingEnabled ? (itemCoaching.length ? itemCoaching : overallCoaching) : []);
    setHeadlineExamples(
      targetingEnabled ? (itemHeadlines.length ? itemHeadlines : overallHeadlineExamples) : []
    );
  }

  // ---------- Event handlers ----------
  const handleTabChange = (_, val) => {
    if (!val) return;
    setModel(val);
    // ensure target is valid for the selected model
    const allowed = modelTargetKeys(val);
    setTargetNeed(allowed.includes(targetNeed) ? targetNeed : allowed[0]);
    clearResultsOnly();
  };

  const handleInputModeToggle = (_, newMode) => {
    if (newMode !== null) setInputMode(newMode);
    setArticle("");
    setUrls([""]);
    clearResultsOnly();
  };

  const addUrlField = () => {
    if (urls.length >= MAX_URLS) return;
    setUrls(prev => [...prev, ""]);
  };
  const removeUrlField = idx => {
    const next = urls.filter((_, i) => i !== idx);
    setUrls(next.length ? next : [""]);
  };
  const updateUrl = (idx, value) => {
    setUrls(prev => prev.map((u, i) => (i === idx ? value : u)));
    clearResultsOnly();
  };

  const canAnalyse = inputMode === "text" ? !!article.trim() : cleanUrls(urls).length > 0;

  const handleAnalyse = async () => {
    if (!canAnalyse) return;
    setLoading(true);
    clearResultsOnly();

    const isUrlMode = inputMode === "url";
    const urlList = isUrlMode ? cleanUrls(urls) : [];

    // Short preface when targeting is on (no control headers)
    const targetPrelude = targetingEnabled
      ? `Target need: ${getNeedLabel(targetNeed)} (${targetNeed}). Include "coaching" (up to 5 concrete actions) and "headlineExamples" (2–3) aligned to this target.\n\n`
      : "";

    // Payload expected by prompts:
    // - text mode: raw text
    // - 1 URL: raw URL string
    // - multi-URL: JSON string {"urls":[...]}
    const body =
      !isUrlMode ? article : urlList.length === 1 ? urlList[0] : JSON.stringify({ urls: urlList });

    try {
      const config = {
        options: {
          model: "gpt-5-mini",
          temperature: 0.2,
          messages: [{ role: "user", content: `${targetPrelude}${body}` }],
          response_format: { type: "json_object" },
          suggestionsEnabled: false
        },
        settings: {
          serviceType:
            model === "news"
              ? "User Needs Categoriser (News Model)"
              : "User Needs Categoriser (Standard Model)",
          articleTone: "Informative",
          language: "English (British English)"
        },
        isTemporaryChat: true
      };

      const response = await completionsApi.sendMessageToCompletionsAPI(config);

      if (response.status === 202 && response.id) {
        let tries = 0;
        let result = null;
        while (tries < 20 && !result) {
          await new Promise(res => setTimeout(res, 1800));
          const poll = await completionsApi.checkTaskStatusInCompletionsQueue(response.id);
          if (poll?.message === "Completed" && poll.output) {
            result = typeof poll.output === "string" ? JSON.parse(poll.output) : poll.output;
            break;
          }
          if (poll?.message === "Failed") throw new Error("Analysis failed.");
          tries++;
        }
        if (!result) throw new Error("Timed out waiting for analysis.");
        handleAnalysisResult(result);
      } else if (response.status === 200 && (response.data || response.message)) {
        let resultRaw = response.data || response.message;
        let result;
        try {
          result = typeof resultRaw === "string" ? JSON.parse(resultRaw) : resultRaw;
          if (typeof result === "string") result = JSON.parse(result);
        } catch (e) {
          result = resultRaw;
        }
        handleAnalysisResult(result);
      } else {
        throw new Error(response.message || "API did not return a result.");
      }
    } catch (err) {
      setComments([err.message || "Analysis failed. Please try again."]);
      setCoaching([]);
      setHeadlineExamples([]);
      setScores(getEmptyScores(model));
      setDominant(null);
      setPerUrlResults([]);
      setActiveView({ type: "overall", index: -1 });
    } finally {
      setLoading(false);
    }
  };

  function handleAnalysisResult(result) {
    // Overall
    const oScores = result.scores || getEmptyScores(model);
    const oDominant = result.dominant || null;

    const baseComments = normaliseStrings(
      Array.isArray(result.comments) && result.comments.length > 0
        ? result.comments
        : [result.summary || "No analysis details returned."]
    );

    const overallCoach =
      normaliseStrings(result.coaching).length
        ? normaliseStrings(result.coaching)
        : normaliseStrings(result.actions).length
          ? normaliseStrings(result.actions)
          : normaliseStrings(
            Array.isArray(result.rewrite_tips) ? result.rewrite_tips : [result.rewrite_tips]
          );

    const overallHeads = normaliseStrings(result.headlineExamples);

    setOverallScores(oScores);
    setOverallDominant(oDominant);
    setOverallComments(baseComments);
    setOverallCoaching(overallCoach);
    setOverallHeadlineExamples(overallHeads);

    // Apply overall initially
    setScores(oScores);
    setDominant(oDominant);
    setComments(baseComments);
    setCoaching(targetingEnabled ? overallCoach : []);
    setHeadlineExamples(targetingEnabled ? overallHeads : []);

    // Per-URL items (support per-item coaching + headlines)
    const per = Array.isArray(result.per_url) ? result.per_url.slice(0, MAX_URLS) : [];
    const normalisedPer = per.map(p => ({
      url: p?.url ?? "",
      scores: p?.scores ?? getEmptyScores(model),
      dominant: p?.dominant ?? null,
      comments: normaliseStrings(p?.comments),
      coaching: normaliseStrings(p?.coaching),
      headlineExamples: normaliseStrings(p?.headlineExamples)
    }));
    setPerUrlResults(normalisedPer);

    if (normalisedPer.length > 0) {
      applyPerUrl(0);
    } else {
      setActiveView({ type: "overall", index: -1 });
    }
  }

  function handleClear() {
    setArticle("");
    setUrls([""]);
    setTargetNeed(firstTargetFor(model));
    setTargetingEnabled(false);
    clearResultsOnly();
  }

  // keep chart shape sane if model changes after analysis
  useEffect(() => {
    if (activeView.type === "per" && perUrlResults[activeView.index]) {
      const item = perUrlResults[activeView.index];
      setScores(item.scores || getEmptyScores(model));
    } else if (activeView.type === "overall") {
      setScores(prev => ({ ...getEmptyScores(model), ...prev }));
    }
    // eslint-disable-next-line
  }, [model]);

  // only allow targets supported by the active model
  const selectableNeedKeys = modelTargetKeys(model);
  const selectableNeeds = USER_NEEDS.filter(n => selectableNeedKeys.includes(n.key));

  const tipText =
    model === "news"
      ? "Tip: Aim for a balance, or focus on a single need. Use action-oriented language for ‘do’, more background for ‘understand’, or strong feeling for ‘feel’."
      : "Tip: Tune your story for a specific need (like ‘connect’ or ‘help’), and see how the motivator profile shifts. This helps with framing and headlines.";

  return (
    <Box sx={{ minHeight: "100vh", color: "#343542", fontFamily: "Google Sans, Roboto, Arial, sans-serif" }}>
      {/* Top toolbar */}
      <Paper
        elevation={1}
        sx={{
          borderRadius: 0,
          px: { xs: 2, md: 6 },
          py: { xs: 2, md: 2.5 },
          mb: { xs: 1.5, md: 4 },
          bgcolor: "#fff",
          borderBottom: "1.5px solid #edeaff",
          boxShadow: "0 2px 10px 0 #34354207",
          top: 0,
          zIndex: 1100
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
            justifyContent: "space-between",
            maxWidth: 1600,
            mx: "auto"
          }}
        >
          <Typography variant="h5" fontWeight={800} color="#343542" letterSpacing={-0.5}>
            User Needs Analyser
          </Typography>
          <Box sx={{ flex: 1 }} />
          <ToggleButtonGroup
            value={model}
            exclusive
            onChange={handleTabChange}
            size="small"
            sx={{
              bgcolor: "#edeaff",
              borderRadius: 4,
              p: 0.5,
              "& .MuiToggleButton-root": {
                border: 0,
                mx: 1,
                fontWeight: 600,
                fontSize: 15,
                px: 2.5,
                color: "#343542",
                bgcolor: "#edeaff",
                transition: "all 0.2s",
                "&:hover": { bgcolor: "#e1dcff" }
              },
              "& .Mui-selected": {
                color: "#fff !important",
                bgcolor: "#343542 !important",
                boxShadow: "0 2px 12px #34354226",
                borderRadius: 3
              }
            }}
          >
            <ToggleButton value="news">News Model</ToggleButton>
            <ToggleButton value="standard">Full Model</ToggleButton>
          </ToggleButtonGroup>

          <FormControlLabel
            control={<Switch checked={showGroupLabels} onChange={e => setShowGroupLabels(e.target.checked)} color="primary" />}
            label="Show group labels"
            sx={{ mx: 2, fontWeight: 700, color: "#343542", ".MuiFormControlLabel-label": { fontWeight: 600 } }}
          />
        </Box>
      </Paper>

      <Grid
        container
        spacing={isMobile ? 2 : 4}
        alignItems="flex-start"
        justifyContent="center"
        sx={{ maxWidth: 1450, mx: "auto", width: "100%", "& > .MuiGrid-item": { pl: 0, pr: 0 } }}
      >
        {/* LEFT: Chart */}
        <Grid item xs={12} md={8.5} sx={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Paper
            ref={chartContainerRef}
            elevation={4}
            sx={{
              mb: 4,
              borderRadius: 5,
              p: { xs: 0.5, md: 4 },
              bgcolor: "#fff",
              minHeight: { xs: 330, md: 680 },
              width: "100%",
              maxWidth: 740,
              mx: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxSizing: "border-box"
            }}
          >
            <UserNeedsOrbitChart
              needs={needs}
              scores={scores}
              dominant={dominant}
              showGroupLabels={showGroupLabels}
              svgSize={chartSize}
            />
          </Paper>
        </Grid>

        {/* RIGHT: Input + Analysis + Info */}
        <Grid item xs={12} md={3.5}>
          <Box sx={{ pt: { xs: 0, md: 2 } }}>
            {/* Input card */}
            <Paper
              elevation={3}
              sx={{
                bgcolor: "#fff",
                borderRadius: 4,
                px: 3,
                py: 3,
                mb: 4,
                boxShadow: "0 2px 18px #e7e1fa40"
              }}
            >
              {/* Input Mode Toggle + Tooltip */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.2 }}>
                <ToggleButtonGroup
                  value={inputMode}
                  exclusive
                  onChange={handleInputModeToggle}
                  size="small"
                  sx={{
                    borderRadius: 2,
                    bgcolor: "#f4f0fa",
                    "& .MuiToggleButton-root": {
                      border: 0,
                      fontWeight: 500,
                      fontSize: 14,
                      color: "#888",
                      px: 2,
                      py: 0.8
                    },
                    "& .Mui-selected": {
                      color: "#343542",
                      bgcolor: "#edeaff"
                    }
                  }}
                >
                  <ToggleButton value="text">Paste Text</ToggleButton>
                  <ToggleButton value="url">
                    <LinkIcon fontSize="small" sx={{ mr: 0.7, mb: -0.2 }} />
                    URL(s)
                  </ToggleButton>
                </ToggleButtonGroup>
                <Tooltip title={tipText} arrow placement="top">
                  <IconButton sx={{ ml: 1, color: "#7c41fd" }}>
                    <InfoOutlinedIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* TEXT MODE */}
              {inputMode === "text" && (
                <TextField
                  multiline
                  minRows={5}
                  maxRows={12}
                  fullWidth
                  variant="outlined"
                  placeholder="Paste or type your article here"
                  sx={{ bgcolor: "#f8f8fc", "& .MuiInputBase-root": { color: "#343542", fontSize: 16 } }}
                  value={article}
                  onChange={e => {
                    setArticle(e.target.value);
                    clearResultsOnly();
                  }}
                  disabled={loading}
                  autoFocus
                />
              )}

              {/* URL MODE (multi up to 5) */}
              {inputMode === "url" && (
                <>
                  <Stack spacing={1.25}>
                    {urls.map((u, i) => (
                      <Box key={i} sx={{ display: "flex", gap: 1 }}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          placeholder={`Paste article URL ${i + 1} (e.g. https://...)`}
                          sx={{ bgcolor: "#f8f8fc", "& .MuiInputBase-root": { color: "#343542", fontSize: 16 } }}
                          value={u}
                          onChange={e => updateUrl(i, e.target.value)}
                          disabled={loading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LinkIcon color="action" />
                              </InputAdornment>
                            )
                          }}
                        />
                        <IconButton
                          aria-label="remove url"
                          onClick={() => removeUrlField(i)}
                          disabled={loading || urls.length === 1}
                          sx={{ alignSelf: "center" }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Box>
                    ))}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Button
                        onClick={addUrlField}
                        disabled={loading || urls.length >= MAX_URLS}
                        startIcon={<AddIcon />}
                        variant="text"
                        sx={{ textTransform: "none", fontWeight: 600 }}
                      >
                        Add URL ({urls.length}/{MAX_URLS})
                      </Button>
                      <Chip size="small" label={`${cleanUrls(urls).length} ready`} />
                    </Box>
                  </Stack>
                </>
              )}

              {/* Targeting controls */}
              <Divider sx={{ my: 2 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={targetingEnabled}
                    onChange={e => setTargetingEnabled(e.target.checked)}
                    color="primary"
                    disabled={loading}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span style={{ fontWeight: 600 }}>Enable targeting</span>
                    <Tooltip
                      arrow
                      placement="top"
                      title="When on, the analysis includes tailored coaching and headline examples for the selected user need."
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </Tooltip>
                  </Box>
                }
                sx={{ ".MuiFormControlLabel-label": { fontWeight: 600 } }}
              />
              <Box sx={{ mt: 1, display: "flex", gap: 1.5, alignItems: "center" }}>
                <Typography sx={{ minWidth: 110, color: targetingEnabled ? "#343542" : "#aaa" }} fontWeight={600}>
                  Target need
                </Typography>
                <Select
                  size="small"
                  value={targetNeed}
                  disabled={!targetingEnabled || loading}
                  onChange={e => setTargetNeed(e.target.value)}
                  sx={{ flex: 1 }}
                >
                  {USER_NEEDS.filter(n => modelTargetKeys(model).includes(n.key)).map(n => (
                    <MenuItem key={n.key} value={n.key}>
                      {n.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              {/* Actions */}
              <Grid container alignItems="center" sx={{ mt: 2 }}>
                <Grid item xs={6}>
                  <Button
                    onClick={handleClear}
                    disabled={(!article && cleanUrls(urls).length === 0) || loading}
                    variant="outlined"
                    sx={{
                      textTransform: "none",
                      color: "#343542",
                      borderColor: "#edeaff",
                      bgcolor: "#f8f8fc",
                      ":hover": { borderColor: "#343542", color: "#fff", bgcolor: "#7c41fd" },
                      fontWeight: 600,
                      borderRadius: 2
                    }}
                  >
                    Clear
                  </Button>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: "right" }}>
                  <Button
                    onClick={handleAnalyse}
                    disabled={!canAnalyse || loading}
                    variant="contained"
                    sx={{
                      px: 3,
                      py: 1.1,
                      fontSize: 17,
                      fontWeight: 700,
                      borderRadius: 2.5,
                      background: "linear-gradient(90deg, #7c41fd 0%, #33b7e1 100%)",
                      color: "#fff",
                      boxShadow: "0 3px 18px #7c41fd29",
                      textTransform: "none",
                      ":hover": {
                        bgcolor: "#5e33b6",
                        background: "linear-gradient(90deg, #5e33b6 0%, #7c41fd 60%, #33b7e1 100%)",
                        boxShadow: "0 6px 28px #7c41fd44"
                      },
                      ":disabled": { background: "#e1dcff", color: "#b1adc9", boxShadow: "none" }
                    }}
                    endIcon={loading && <CircularProgress color="inherit" size={18} />}
                  >
                    Analyse {inputMode === "url" && cleanUrls(urls).length > 1 ? "URLs" : "Article"}
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* Per-URL switcher */}
            {perUrlResults.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  mb: 2,
                  bgcolor: "#fff",
                  border: "1px solid #edeaff",
                  borderRadius: 2.5,
                  px: 2,
                  py: 1.5
                }}
              >
                <Typography fontSize={14} fontWeight={700} sx={{ mb: 1 }}>
                  Viewing: {activeView.type === "overall" ? "Overall" : `Article ${activeView.index + 1}`}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  <Chip
                    label="Overall"
                    variant={activeView.type === "overall" ? "filled" : "outlined"}
                    onClick={applyOverall}
                    sx={{ fontWeight: 600 }}
                  />
                  {perUrlResults.map((item, i) => (
                    <Chip
                      key={i}
                      label={`${i + 1}: ${getHost(item.url)}`}
                      variant={activeView.type === "per" && activeView.index === i ? "filled" : "outlined"}
                      onClick={() => applyPerUrl(i)}
                      sx={{ fontWeight: 600 }}
                    />
                  ))}
                </Box>
              </Paper>
            )}

            {/* Analysis/comments */}
            <Paper
              elevation={0}
              sx={{
                mb: 4,
                bgcolor: "#f7f7fa",
                border: "1.5px solid #edeaff",
                borderRadius: 3.5,
                px: 3,
                py: 2.5,
                color: "#343542",
                minHeight: 88
              }}
            >
              <Typography fontSize={17} fontWeight={700} color="#343542" sx={{ mb: 1, letterSpacing: 0.2 }}>
                {loading ? "Analysing..." : comments.length ? "Analysis & Comments" : "Awaiting analysis"}
              </Typography>
              {comments.length > 0 && (
                <Box component="ul" sx={{ pl: 3, mb: 0, color: "#3e415b", fontSize: 15 }}>
                  {comments.map((c, i) => (
                    <li key={i} style={{ marginBottom: 3 }}>
                      {c}
                    </li>
                  ))}
                </Box>
              )}
              {!comments.length && !loading && (
                <Typography fontSize={15} color="#888" sx={{ mt: 1 }}>
                  Comments and insights will appear here after you analyse an article or URL.
                </Typography>
              )}
              {loading && <CircularProgress size={28} sx={{ mt: 1, color: "#343542" }} />}
            </Paper>

            {/* Targeting feedback */}
            {targetingEnabled && (
              <Paper
                elevation={0}
                sx={{
                  mb: 4,
                  bgcolor: "#fcfcfe",
                  border: "1.5px dashed #cfc9ff",
                  borderRadius: 3.5,
                  px: 3,
                  py: 2.5,
                  color: "#343542"
                }}
              >
                <Typography fontSize={17} fontWeight={800} sx={{ mb: 1 }}>
                  Action plan — target: {getNeedLabel(targetNeed)}
                </Typography>
                {coaching.length ? (
                  <Box component="ol" sx={{ pl: 3, mb: 2, color: "#3e415b", fontSize: 15 }}>
                    {coaching.map((tip, i) => (
                      <li key={i} style={{ marginBottom: 6 }}>
                        {tip}
                      </li>
                    ))}
                  </Box>
                ) : (
                  <Typography fontSize={15} color="#888" sx={{ mb: 2 }}>
                    Concrete suggestions will appear here when available.
                  </Typography>
                )}

                {/* Headline rewrites */}
                {headlineExamples.length > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography fontSize={16} fontWeight={700} sx={{ mb: 1 }}>
                      Headline rewrites (aligned to target)
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 0, color: "#3e415b", fontSize: 15 }}>
                      {headlineExamples.map((h, i) => (
                        <li key={i} style={{ marginBottom: 4 }}>
                          {h}
                        </li>
                      ))}
                    </Box>
                  </>
                )}
              </Paper>
            )}

            {/* Info/Help box */}
            <Paper
              sx={{
                bgcolor: "#fcfcfe",
                border: `2px solid #edeaff`,
                borderRadius: 4,
                px: 3.5,
                py: 3.2,
                mb: 4,
                color: "#343542",
                boxShadow: "0 2px 12px #edeaff44"
              }}
            >
              <Typography fontWeight={700} fontSize={18} color="#343542" sx={{ mb: 1 }}>
                What is this tool?
              </Typography>
              <Typography fontSize={15.6} sx={{ color: "#474763", lineHeight: 1.65 }}>
                The User Needs Analyser helps you understand which user needs and motivations dominate your content — paste
                full text or provide one or more URLs. Enable targeting to get concrete edits and headline rewrites for a specific user need.
              </Typography>
            </Paper>
          </Box>
        </Grid>
      </Grid>

      {/* Footer */}
      <Box sx={{ mt: 5, color: "#bbb", textAlign: "center", fontSize: 14 }}>
        User Needs Model 2.0 © 2023 Smartocto, licensed under CC BY-SA 4.0
      </Box>
    </Box>
  );
}
